import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import { AuditLogContext, useAuditLogHistory } from "@ryde/redux";
import {
  getAuditLogHistoryGaps,
  IHistoryLogDrawerProps,
  isGlobalLoaderActiveSelector,
  normalizeErrors,
} from "@ryde/shared";

import {
  LayoutRightLoader,
  LayoutRightLoaderSlotStyled,
} from "../../../../right";
import { SearchSection } from "../../drawer";
import { RideHistoryLogGrid } from "./grid";

interface IAuditLogTabContentProps {
  id: number;
  entity: IHistoryLogDrawerProps["entity"];
}

export const AuditLogTabContent: FC<IAuditLogTabContentProps> = ({
  id,
  entity,
}) => {
  const { t } = useTranslation();
  const isGlobalLoaderActive = useRecoilValue(isGlobalLoaderActiveSelector);
  const {
    auditLogs,
    filteredAuditLogs,
    loading,
    error,
    contextValue,
    searchSectionProps,
  } = useAuditLogHistory({ id, entity });

  const { hasRowsWithoutRemovals, isMissingCreation } = useMemo(
    () => getAuditLogHistoryGaps(auditLogs),
    [auditLogs],
  );

  return (
    <AuditLogContext.Provider value={contextValue}>
      {auditLogs.length > 0 && <SearchSection {...searchSectionProps} />}
      {!loading && (hasRowsWithoutRemovals || isMissingCreation) && (
        <div className="ride-history-log__notices">
          {isMissingCreation && (
            <span>{t("auditLog.history.missingCreation")}</span>
          )}
          {hasRowsWithoutRemovals && (
            <span>{t("auditLog.history.removalsNotRecorded")}</span>
          )}
        </div>
      )}
      {error && !loading && (
        <ErrorsDisplayContainer
          marginLeft="22px"
          serverErrors={normalizeErrors(error, t)}
        />
      )}
      {!loading && <RideHistoryLogGrid logs={filteredAuditLogs} />}
      {loading && !isGlobalLoaderActive && (
        <LayoutRightLoaderSlotStyled>
          <LayoutRightLoader>{t("ui.LOADING")}...</LayoutRightLoader>
        </LayoutRightLoaderSlotStyled>
      )}
    </AuditLogContext.Provider>
  );
};
